import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLogin, usePostHog } from "@finitless/shared/hooks";
import { useApiStore } from "@finitless/shared/stores";
import {
  isWhiteLabel,
  getWhiteLabelProject,
  getBrandLogo,
  getBrandName,
} from "@finitless/shared/lib";
import finitlessLogo from "../../assets/finitless-logo.png";
import { useToastContext } from "@finitless/shared/contexts";
import { useTranslation } from "react-i18next";
import { useUiLocale } from "../../hooks/useUiLocale";
import { getLoginPrefillFromEnv } from "../../lib/loginPrefill";
import { Input, LocaleSelector } from "../ui";

const LoginPage = ({ onLoginSuccess, logo = finitlessLogo }) => {
  const { t } = useTranslation(["auth", "app", "toasts"]);
  const { locale, setLocale, supportedLocales } = useUiLocale();
  const defaults = getLoginPrefillFromEnv();
  const whiteLabel = isWhiteLabel();
  const whiteLabelProject = getWhiteLabelProject();
  const brandLogo = getBrandLogo(logo);
  const [email, setEmail] = useState(defaults.email);
  const [password, setPassword] = useState(defaults.password);
  const [project, setProject] = useState(whiteLabelProject || defaults.project);
  const [showPassword, setShowPassword] = useState(false);
  const loginMutation = useLogin();
  const setProjectConfig = useApiStore((state) => state.setProject);
  const toast = useToastContext();
  const navigate = useNavigate();
  const { trackEngagement } = usePostHog();

  const handleLocaleChange = async (event) => {
    await setLocale(event.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email || !password || !project) {
      toast.error(t("validation.fillAllFields", { ns: "auth" }));
      return;
    }

    try {
      // Set the project configuration before login
      setProjectConfig(project);

      const result = await loginMutation.mutateAsync({ email, password });

      // Track login event
      trackEngagement.login({
        user_id: result?.id,
        email,
        project,
      });

      toast.success(t("auth.loginSuccess", { ns: "toasts" }));
      if (onLoginSuccess) {
        onLoginSuccess(result);
      }
    } catch (error) {
      toast.error(error.message || t("auth.loginFailed", { ns: "toasts" }));
    }
  };

  return (
    <div className="login-page flex min-h-screen items-center justify-center bg-black px-4 py-4 sm:px-6 sm:py-8 lg:px-8">
      <div className="w-full max-w-md space-y-4 sm:space-y-6">
        <LocaleSelector
          id="auth-locale-selector"
          label={t("language.label", { ns: "app" })}
          showLabel={false}
          showLeadingIcon={false}
          value={locale}
          onChange={handleLocaleChange}
          options={supportedLocales}
          containerClassName="w-full justify-end"
          selectClassName="h-9 min-w-[124px] border-white/15 bg-slate-950/70 text-[13px] shadow-none"
        />

        {/* Logo and Header */}
        <div className="text-center">
          <div className="mx-auto mb-4 flex items-center justify-center sm:mb-6">
            {brandLogo && (
              <img
                className="h-5 w-auto max-w-sm sm:h-8"
                src={brandLogo}
                alt="Finitless"
              />
            )}
          </div>
          <h2 className="text-2xl font-semibold text-white sm:text-3xl">
            {t("welcomeTitle", {
              ns: "auth",
              brand: getBrandName("Finitless"),
            })}
          </h2>
          <p className="mt-2 text-sm text-gray-400">
            {t("welcomeSubtitle", { ns: "auth" })}
          </p>
        </div>

        {/* Login Form */}
        <form className="mt-6 space-y-3" onSubmit={handleSubmit}>
          <div className="space-y-3">
            {!whiteLabel && (
              <Input
                id="project"
                name="project"
                type="text"
                autoComplete="off"
                required
                value={project}
                onChange={(e) => setProject(e.target.value)}
                placeholder={t("placeholders.project", { ns: "auth" })}
                disabled={loginMutation.isPending}
                label={t("fields.projectName", { ns: "auth" })}
                variant="dark"
                size="lg"
                labelClassName="text-gray-300"
              />
            )}

            {/* Email Field */}
            <Input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("placeholders.email", { ns: "auth" })}
              disabled={loginMutation.isPending}
              label={t("fields.emailAddress", { ns: "auth" })}
              variant="dark"
              size="lg"
              labelClassName="text-gray-300"
            />

            {/* Password Field */}
            <Input
              id="password"
              name="password"
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t("placeholders.password", { ns: "auth" })}
              disabled={loginMutation.isPending}
              label={t("fields.password", { ns: "auth" })}
              variant="dark"
              size="lg"
              labelClassName="text-gray-300"
              rightIcon={
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={loginMutation.isPending}
                  aria-label={
                    showPassword
                      ? t("accessibility.hidePassword", { ns: "auth" })
                      : t("accessibility.showPassword", { ns: "auth" })
                  }
                  className="text-gray-400 transition-colors hover:text-gray-300"
                >
                  {showPassword ? (
                    <svg
                      className="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"
                      />
                    </svg>
                  ) : (
                    <svg
                      className="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                      />
                    </svg>
                  )}
                </button>
              }
            />
          </div>

          {/* Submit Button */}
          <div>
            <button
              type="submit"
              disabled={loginMutation.isPending}
              className="group relative flex w-full justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-normal text-white transition-colors hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loginMutation.isPending ? (
                <div className="flex items-center">
                  <svg
                    className="mr-3 -ml-1 h-5 w-5 animate-spin text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  {t("loggingIn", { ns: "auth" })}
                </div>
              ) : (
                t("loginButton", { ns: "auth" })
              )}
            </button>
          </div>

          {/* Forgot Password Link */}
          <div className="text-center">
            <button
              type="button"
              onClick={() => navigate("/password/forgot")}
              disabled={loginMutation.isPending}
              className="text-sm text-blue-500 transition-colors hover:text-blue-400 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t("forgotPasswordLink", { ns: "auth" })}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LoginPage;
